import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QuickRecallCard } from '@libertify/ui';
import { assetUrl, writeJson } from '../addOnAssets';
import { quickRecallKey } from './useQuickRecall';
import type {
  QuickRecallDeck,
  QuickRecallSaver,
  UseQuickRecallArgs,
} from './types';
import type { AssetPatchBody } from '../../types';

/**
 * Writes a project's Quick recall deck back to its asset:
 * `PATCH /media/assets/{assetId}` with `{ add_on: { cards } }`.
 *
 * The deck is written into the query cache when the save STARTS, not when it
 * succeeds, and the cache is not invalidated. Invalidating refetches every
 * mounted `useQuickRecall`, and that read is a three-request chain (project →
 * asset → the asset's signed path) whose result an open editor cannot even use
 * — it seeds its rows once, at mount. This is a whole-asset replace, so what
 * was sent is what is stored: the cache is already correct, and a card reopened
 * later seeds from it without a request.
 *
 * Seeding at the start is what makes a card reopened MID-FLIGHT correct. These
 * cards unmount on every rail switch and flush their debounced save on the way
 * out, so a PATCH is routinely open with no card mounted. Seeding on success
 * instead would leave the cache pre-edit for that whole flight: switching back
 * before it lands would seed the rows from stale data, and the next save would
 * ship the pre-edit deck back over the edit that was in flight.
 *
 * On failure the cache goes back to what it held before the write. Not a
 * re-read: a PATCH that failed for want of network would take the re-read
 * down with it, and a read error unmounts the editor — with everything typed.
 */
export function useSaveQuickRecall({
  apiBaseUrl,
  projectId,
  token,
}: UseQuickRecallArgs): QuickRecallSaver {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      assetId,
      cards,
    }: {
      assetId: string;
      cards: QuickRecallCard[];
    }) =>
      writeJson(
        assetUrl(apiBaseUrl, assetId),
        { add_on: { cards } } satisfies AssetPatchBody,
        token,
      ),
    onMutate: async ({ assetId, cards }) => {
      const queryKey = quickRecallKey(apiBaseUrl, projectId);
      // A read still in flight would land after this write and put the
      // pre-edit deck back.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<QuickRecallDeck>(queryKey);
      queryClient.setQueryData<QuickRecallDeck>(queryKey, { cards, assetId });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      const queryKey = quickRecallKey(apiBaseUrl, projectId);
      // setQueryData ignores `undefined`, so an empty cache is emptied again
      // rather than left holding the rejected deck.
      if (context?.previous) {
        queryClient.setQueryData<QuickRecallDeck>(queryKey, context.previous);
      } else {
        queryClient.removeQueries({ queryKey });
      }
    },
  });

  return {
    save: (assetId, cards) => mutation.mutateAsync({ assetId, cards }),
    isSaving: mutation.isLoading,
  };
}
